import type { Receipt, ReceiptLotAddress } from '../api/types';

/** Cents in, "$24.35" out. The API never sends floating-point dollars. */
export function usd(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}$${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

const pad = (n: number) => String(n).padStart(2, '0');

type Translate = (key: string, options?: Record<string, unknown>) => string;

// Wall-clock rendering lives in lot-time.ts: every visible time must carry the
// lot's timezone, so no device-clock formatter is allowed to exist here.

/** H:MM:SS countdown. Seconds are the point — the driver is watching them. */
export function countdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  return `${h}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

/**
 * Quote-hold countdown: "09:59", not "0:09:59" — holds run minutes, so a
 * leading hour digit is noise; it appears only if a hold really is that long.
 */
export function holdCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const clock = `${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
  return h > 0 ? `${h}:${clock}` : clock;
}

/** "K928471" → "K92 8471"; grouping is display-only, never sent to the API. */
export function plateDisplay(plate: string): string {
  if (plate.length <= 4) return plate;
  const split = Math.ceil(plate.length / 2);
  return `${plate.slice(0, split)} ${plate.slice(split)}`;
}

/**
 * Duration labels are formatted on the client, not taken from the API.
 *
 * The server returns Chinese strings like "2 小时" for historical reasons; an
 * English UI must not render those, so anything user-visible is derived from
 * the minute count instead.
 */
export function durationLabel(minutes: number, t: Translate): string {
  if (minutes === 1440) return t('units.fullDay');
  if (minutes < 60) return t('units.minutes', { count: minutes });
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? t('units.hours', { count: h }) : t('units.hoursMinutes', { hours: h, minutes: m });
}

/** A run of rate tiers sharing one price: (fromMinutes, toMinutes] inclusive. */
export interface RateBand {
  fromMinutes: number;
  toMinutes: number;
  totalCents: number;
}

/**
 * Presentation-only merge of the API's rate card. The per-tier `label` is a
 * server display string and must never render; consecutive tiers priced the
 * same (a daily cap reached early) must read as one band, not a config error.
 * The tier data itself is untouched — quotes still come from the backend.
 */
export function mergeRateTiers(tiers: ReadonlyArray<{ minutes: number; totalCents: number }>): RateBand[] {
  const sorted = [...tiers].sort((a, b) => a.minutes - b.minutes);
  const bands: RateBand[] = [];
  for (const tier of sorted) {
    const last = bands[bands.length - 1];
    if (last && last.totalCents === tier.totalCents) {
      last.toMinutes = tier.minutes;
    } else {
      bands.push({
        fromMinutes: last?.toMinutes ?? 0,
        toMinutes: tier.minutes,
        totalCents: tier.totalCents,
      });
    }
  }
  return bands;
}

/** The API sends raw address parts only; the one-line display form is client-composed. */
export function lotAddressLabel(address: ReceiptLotAddress): string {
  const region = address.postalCode ? `${address.state} ${address.postalCode}` : address.state;
  return `${address.line}, ${address.city}, ${region}`;
}

/**
 * "Visa •••• 4242" from the provider's lowercase brand code. Known brands get
 * their proper mark via the bundle; an unknown code renders as-is rather than
 * dropping the tender information.
 */
export function cardLabel(
  brand: string | null | undefined,
  last4: string | null | undefined,
  t: Translate,
  exists: (key: string) => boolean,
): string | null {
  if (!brand && !last4) return null;
  const name = brand ? (exists(`cards.${brand}`) ? t(`cards.${brand}`) : brand) : null;
  return [name, last4 ? `•••• ${last4}` : null].filter(Boolean).join(' ');
}

export function receiptLineLabel(line: Receipt['lines'][number], t: Translate): string {
  const first = line.requestedMinutesAdded === line.cumulativeRequestedMinutes;
  const requested = durationLabel(line.requestedMinutesAdded, t);
  const entitlement = durationLabel(line.minutesAdded, t);
  const total = durationLabel(line.cumulativeMinutes, t);
  const rounded = line.requestedMinutesAdded !== line.minutesAdded;

  if (first) {
    return rounded
      ? t('receipt.lineFirstRounded', { requested, entitlement })
      : t('receipt.lineFirst', { duration: entitlement });
  }
  return rounded
    ? t('receipt.lineExtensionRounded', { requested, entitlement, total })
    : t('receipt.lineExtension', { added: entitlement, total });
}
